import './index.css'
import { useState, useRef } from 'react'
import { Plus } from 'lucide-react'
import { useGoals } from '../../hooks/useGoals'
import GoalCard from '../GoalCard'
import AddGoalSheet from '../AddGoalSheet'
import GoalDetailSheet from '../GoalDetailSheet'
import type { Goal, GoalsPanelProps } from '../../types'
import { getToastErrorMessage, useToast } from '../Toast'

export default function GoalsPanel({ householdId }: GoalsPanelProps) {
  const toast = useToast()
  const { goals, loading, error, addGoal, editGoal, removeGoal, refetch } = useGoals(householdId)

  const [sheetOpen,       setSheetOpen]       = useState(false)
  const [editingGoal,     setEditingGoal]     = useState<Goal | null>(null)
  const [detailGoal,      setDetailGoal]      = useState<Goal | null>(null)
  const [animatingGoalId, setAnimatingGoalId] = useState<string | null>(null)
  const animTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  function openCreate() {
    setEditingGoal(null)
    setSheetOpen(true)
  }

  function openEdit(goal: Goal) {
    setEditingGoal(goal)
    setSheetOpen(true)
  }

  async function handleSheetSubmit(payload: { name: string; target_amount: number; emoji?: string | null; deadline?: string | null }) {
    try {
      if (editingGoal) {
        await editGoal(editingGoal.id, payload)
        toast.success('Meta atualizada.')
      } else {
        await addGoal(payload)
        toast.success('Meta criada.')
      }
    } catch (err) {
      toast.error(getToastErrorMessage(err, 'Erro ao salvar meta.'))
      throw err
    }
  }

  async function handleDelete(id: string) {
    if (!confirm('Excluir esta meta e todas as contribuições?')) return
    try {
      await removeGoal(id)
      toast.success('Meta excluída.')
    } catch (err) {
      toast.error(getToastErrorMessage(err, 'Erro ao excluir meta.'))
    }
  }

  function handleContributionAdded(goalId: string) {
    setDetailGoal(null)
    refetch()
    if (animTimerRef.current) clearTimeout(animTimerRef.current)
    setAnimatingGoalId(goalId)
    animTimerRef.current = setTimeout(() => setAnimatingGoalId(null), 1200)
  }

  return (
    <div className="bc-goals">
      <div className="bc-goals__header">
        <h2 className="bc-goals__heading">Metas</h2>
        <button className="bc-btn bc-btn--primary bc-btn--sm" onClick={openCreate}>
          <Plus size={16} />
          Nova meta
        </button>
      </div>

      {error && <p className="bc-field__error">{error}</p>}

      {loading && goals.length === 0 && (
        <p className="bc-loading-text">Carregando metas…</p>
      )}

      {!loading && goals.length === 0 && (
        <div className="bc-goals__empty">
          <p>Nenhuma meta ainda.</p>
          <button className="bc-btn bc-btn--primary" onClick={openCreate}>
            Criar primeira meta
          </button>
        </div>
      )}

      {goals.length > 0 && (
        <div className="bc-goals__grid">
          {goals.map(goal => (
            <GoalCard
              key={goal.id}
              goal={goal}
              animating={animatingGoalId === goal.id}
              onEdit={() => openEdit(goal)}
              onViewDetail={() => setDetailGoal(goal)}
              onDelete={() => handleDelete(goal.id)}
            />
          ))}
        </div>
      )}

      <AddGoalSheet
        open={sheetOpen}
        editing={editingGoal}
        onClose={() => setSheetOpen(false)}
        onSubmit={handleSheetSubmit}
      />

      <GoalDetailSheet
        open={detailGoal !== null}
        goal={detailGoal}
        onClose={() => setDetailGoal(null)}
        onContributionAdded={handleContributionAdded}
      />
    </div>
  )
}
